import { useCallback, useRef } from 'react';

import { useAppSelector } from '@/store/hooks';
import { logger } from '@/services/logger/logger';
import { TransactionState } from '@/features/paymentProcess/transactionSlice';
import {
  MarketingPreferencesUpdateRequest,
  MarketingPreferencesUpdateResponse,
  useUpdateMarketingPreferencesMutation,
} from '@/api/payments/updateMarketingPreferences';

import { MarketingPreferencesData } from './types';

const buildPayload = (
  transactionState: TransactionState,
  formData: MarketingPreferencesData,
): MarketingPreferencesUpdateRequest => ({
  transactionId: transactionState.transactionId || null,
  subscriptionId: transactionState.subscriptionId || null,
  permissionEmail: (formData.mp_permissionEmail === 'yes') ? 1 : null,
  permissionPost: (formData.mp_permissionPost === 'yes') ? 1 : null,
  permissionPhone: (formData.mp_permissionPhone === 'yes') ? 1 : null,
  permissionSMS: (formData.mp_permissionSMS === 'yes') ? 1 : null,
  // Strips out whitespace to meet the B/E validation requirements
  phone: formData.mp_permissionPhone ? formData.mp_phone.replace(/\s+/g, '') : null,
  mobile: formData.mp_permissionSMS ? formData.mp_mobile.replace(/\s+/g, '') : null,
});

export function useUpdateMarketingPreferences() {
  const transactionState = useAppSelector(state => state.transaction);

  const [marketingPreferencesUpdateTrigger] = useUpdateMarketingPreferencesMutation();

  const onSuccess = useRef<((data: MarketingPreferencesUpdateResponse) => void) | undefined>(undefined);
  const onError = useRef<((error?: unknown) => void) | undefined>(undefined);

  // Trigger payment-service update
  const triggerUpdateApi = useCallback(async (
    formPrefsData: MarketingPreferencesData,
    callbacks?: {
      onSuccess?: (data: MarketingPreferencesUpdateResponse) => void,
      onError?: (error?: unknown) => void,
    }
  ) => {
    onSuccess.current = callbacks?.onSuccess;
    onError.current = callbacks?.onError;

    let result;

    try {
      const payload = buildPayload(
        transactionState,
        formPrefsData
      );
      result = await marketingPreferencesUpdateTrigger(payload);
    } catch (err) {
      logger.error('Error processing Marketing preferences');
      logger.error(err);
      if (onError.current) {
        onError.current(err);
      }
    }

    // Handle payment-service update errors
    if (!!result?.error || result?.data?.message !== 'Marketing preferences submission has been processed') {
      logger.error('Error processing Marketing preferences');
      logger.error(result?.error);
      if (onError.current) {
        onError.current(result?.error);
      }
      return null;
    }

    // Handle payment-service update complete
    if (onSuccess.current) {
      onSuccess.current(result?.data);
    }

    return result?.data;
  }, [marketingPreferencesUpdateTrigger, transactionState]);

  return {
    triggerUpdateApi
  };
}
